import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EventoService from '../../services/eventoService';
import NotificacionService from '../../services/notificacionService';
import CrearEventoModal from '../../components/CrearEventoModal';
import BannerEstadoFinal from '../../components/BannerEstadoFinal';
import ChecklistTramite from '../../components/Tramite/ChecklistTramite';
import BotonCancelarReactivarTramite from '../../components/Tramite/BotonCancelarReactivarTramite';
import TarjetaCobroTramite from '../../components/Cobranzas/TarjetaCobroTramite';
import SeccionCobranzaTramite from '../../components/Cobranzas/SeccionCobranzaTramite';
import HistorialTramite from '../../components/HistorialTramite';
import PanelAccionesAvanzadasTramite from '../../components/Administracion/PanelAccionesAvanzadasTramite';
import WhatsAppHeaderButton from '../../components/WhatsAppHeaderButton';
import PanelUbicacionTramite from '../../components/PanelUbicacionTramite';
import FeedbackButton from '../../components/FeedbackButton';
import NotaComentario from '../../components/NotaComentario';
import TarjetaTurnoAnses from '../../components/TarjetaTurnoAnses';
import ExpedientesAnsesTramite, { enSeguimientoDeExpediente } from '../../components/ExpedientesAnsesTramite';
import useCustomModal from '../../hooks/useCustomModal';
import useVolverAtras from '../../hooks/useVolverAtras';
import CustomModal from '../../components/CustomModal';
import { useAuth } from '../../auth/AuthContext';
import { ModalCrearTurno } from '../Recepcion/TurneroPage';
import turneroStyles from '../../style/pages/Turnero.module.css';
import { getTramiteReconocimientoById, actualizarChecklistItem, actualizarTramiteReconocimiento, actualizarComentariosReconocimiento, guardarResolucionReconocimiento } from '../../services/reconocimientoService';
import { formatearTotalReconocido } from '../../utils/totalReconocido';
import { subirArchivo, descargarArchivo, obtenerArchivosPorTramite, eliminarArchivo } from '../../services/archivoService';
import { apellidoNombre } from '../../utils/nombrePersona';
import EtiquetaTramite from '../../components/EtiquetaTramite';
import BotonGenerarCaratula from '../../components/Caratula/BotonGenerarCaratula';

const LABEL_SUBTIPO = { COMUN: 'Común', SERVICIO_DOMESTICO: 'Servicio Doméstico' };
const RESULTADO_META = {
    FAVORABLE: { label: 'Favorable', bg: '#dcfce7', color: '#166534' },
    DESFAVORABLE: { label: 'Desfavorable', bg: '#fee2e2', color: '#991b1b' },
    PENDIENTE: { label: 'Pendiente', bg: '#fef3c7', color: '#92400e' },
};

// Ruta de la ficha del trámite padre (Jubilación o Plan de Pago) para volver ahí en vez del
// listado de Reconocimiento, cuando este reconocimiento nació embebido desde ese padre.
const RUTA_PADRE_POR_TIPO = { JUBILACION: '/jubilacion/tramites', PLAN_PAGO: '/plan-pago/tramites' };

// Banner grande de resultado (patrón ART/RTI: alert vistoso cuando el trámite llega a un
// resultado final, acá derivado de la resolución en vez de un estado de flujo).
const RESULTADO_BANNER = {
    FAVORABLE: { bg: 'success', icon: <CheckCircleIcon style={{ fontSize: '2.5rem' }} />, titulo: 'Reconocimiento de Servicios Favorable' },
    DESFAVORABLE: { bg: 'danger', icon: <CancelIcon style={{ fontSize: '2.5rem' }} />, titulo: 'Reconocimiento de Servicios Desfavorable' },
};

const formatLocalFecha = (fechaInput) => {
    if (!fechaInput) return '';
    if (typeof fechaInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaInput)) return fechaInput;
    const d = new Date(fechaInput);
    if (Number.isNaN(d.getTime())) return '';
    return new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
};

// Clase del badge contador (X/Y): verde cuando está completo, gris en caso contrario.
const claseBadgeContador = (completados, total) => (total > 0 && completados === total ? 'bg-success' : 'bg-secondary');

// Los inputs numéricos devuelven string; la resolución guarda enteros o null (todo es opcional).
const aEnteroONulo = (valor) => {
    if (valor === '' || valor === null || valor === undefined) return null;
    const n = parseInt(valor, 10);
    return Number.isNaN(n) ? null : n;
};

function VistaDetalladaTramiteReconocimiento() {
    const { idTramite } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { modalState, hideModal, showModal } = useCustomModal();

    const [tramite, setTramite] = useState(null);

    // El hook va acá arriba y no junto al botón: más abajo hay early returns por loading/error,
    // y llamarlo después de ellos rompería el orden de hooks. El fallback se recalcula solo
    // cuando `tramite` carga.
    const rutaBasePadre = RUTA_PADRE_POR_TIPO[tramite?.nombreTipoTramitePadre];
    const volverAtras = useVolverAtras(
        (tramite?.idTramitePadre && rutaBasePadre)
            ? `${rutaBasePadre}/${tramite.idTramitePadre}`
            : '/tramites'
    );
    const [loading, setLoading] = useState(true);

    // Edición de datos del trámite (no el cliente ni el subtipo, que es fijo desde la creación)
    const [editMode, setEditMode] = useState(false);
    const [tramiteEdit, setTramiteEdit] = useState(null);
    const [guardandoDatos, setGuardandoDatos] = useState(false);

    // Herramienta: crear turno prellenado con los datos del cliente del trámite
    const [mostrarModalTurno, setMostrarModalTurno] = useState(false);

    // Resolución del reconocimiento (total reconocido en años/meses/días, cuánto falta,
    // resultado, fecha, observaciones)
    const [resolucionEditMode, setResolucionEditMode] = useState(false);
    const [resolucionEdit, setResolucionEdit] = useState(null);
    const [guardandoResolucion, setGuardandoResolucion] = useState(false);
    const [subiendoResolucion, setSubiendoResolucion] = useState(false);

    const [eventos, setEventos] = useState([]);
    const [cargandoEventos, setCargandoEventos] = useState(true);
    const [mostrarModalEvento, setMostrarModalEvento] = useState(false);
    const [fechaSeleccionadaEvento, setFechaSeleccionadaEvento] = useState(new Date());

    const [modalHistorialAbierto, setModalHistorialAbierto] = useState(false);
    const [subiendoItem, setSubiendoItem] = useState(null);

    // Archivos del trámite (fuente única: el checklist enlaza por idArchivo; "otros" son los no vinculados)
    const [archivos, setArchivos] = useState([]);
    const [subiendoGeneral, setSubiendoGeneral] = useState(false);

    const recargarTramite = useCallback(async () => {
        try {
            const data = await getTramiteReconocimientoById(idTramite);
            setTramite(data);
        } catch (error) {
            console.error('Error al cargar trámite de Reconocimiento de Servicios:', error);
        }
    }, [idTramite]);

    const recargarArchivos = useCallback(async () => {
        try {
            const data = await obtenerArchivosPorTramite(idTramite);
            setArchivos(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error al cargar archivos:', error);
        }
    }, [idTramite]);

    const recargarEventos = useCallback(async () => {
        try {
            const response = await EventoService.getEventosPorTramite(idTramite);
            setEventos(response.data || []);
        } catch (error) {
            console.error('Error recargando eventos:', error);
        }
    }, [idTramite]);

    // Cargar o cancelar un turno ANSES mueve el estado del trámite del lado del backend
    // (TurnoAnsesService.moverEstadoTramite), así que no alcanza con recargar los eventos:
    // sin recargar la carpeta, el badge de estado y el banner de cierre quedan viejos hasta un F5.
    const refrescarPorTurnoAnses = useCallback(async () => {
        await Promise.all([recargarTramite(), recargarEventos()]);
    }, [recargarTramite, recargarEventos]);

    useEffect(() => {
        const cargar = async () => {
            setLoading(true);
            setCargandoEventos(true);
            try {
                const [tramiteResult, eventosResult, archivosResult] = await Promise.allSettled([
                    getTramiteReconocimientoById(idTramite),
                    EventoService.getEventosPorTramite(idTramite),
                    obtenerArchivosPorTramite(idTramite),
                ]);
                setTramite(tramiteResult.status === 'fulfilled' ? tramiteResult.value : null);
                setEventos(eventosResult.status === 'fulfilled' ? (eventosResult.value.data || []) : []);
                setArchivos(archivosResult.status === 'fulfilled' && Array.isArray(archivosResult.value) ? archivosResult.value : []);
            } finally {
                setLoading(false);
                setCargandoEventos(false);
            }
        };
        cargar();
    }, [idTramite]);

    // Auto-refresco de eventos (paridad con ART/RTI/Jubilación/PUAM)
    useEffect(() => {
        const handleEventoCreado = () => recargarEventos();
        window.addEventListener('eventoCreadoDesdeChat', handleEventoCreado);
        const interval = setInterval(() => recargarEventos(), 30000);
        return () => {
            window.removeEventListener('eventoCreadoDesdeChat', handleEventoCreado);
            clearInterval(interval);
        };
    }, [recargarEventos]);

    const handleGuardarEvento = async (evento) => {
        try {
            const responseEvento = await EventoService.crearEvento({
                ...evento,
                fecha: formatLocalFecha(evento.fecha),
                idTramite: Number(idTramite),
            });
            const idEventoCreado = responseEvento.data.idEvento || responseEvento.data;
            if (evento.tiemposRecordatorio && evento.tiemposRecordatorio.length > 0) {
                await NotificacionService.programarNotificacionesAutomaticas(idEventoCreado, evento.tiemposRecordatorio);
            }
            window.dispatchEvent(new CustomEvent('eventoCreadoDesdeChat', { detail: { timestamp: Date.now(), source: 'tramite-reconocimiento' } }));
            await recargarEventos();
            setMostrarModalEvento(false);
            toast.success('Evento creado correctamente');
        } catch (error) {
            console.error('Error al crear evento:', error);
            toast.error('Error al crear el evento');
        }
    };

    const formatearFechaEvento = (fechaString) => {
        if (!fechaString) return '';
        const [y, m, d] = fechaString.split('T')[0].split('-');
        return `${d}/${m}/${y}`;
    };

    // ---- Descarga con nombre real (extensión + tipo correctos) ----
    const descargarPorId = async (idArchivo) => {
        try {
            const meta = archivos.find(a => a.idArchivo === idArchivo);
            const nombre = meta?.nombre || `archivo_${idArchivo}`;
            const resp = await descargarArchivo(idArchivo);
            const blob = meta?.tipo ? new Blob([resp.data], { type: meta.tipo }) : new Blob([resp.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = nombre;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error al descargar archivo:', err);
            toast.error('No se pudo descargar el archivo');
        }
    };

    // ---- Checklist ----
    const togglePresentado = async (item) => {
        if (item.idArchivo) return; // con archivo adjunto, el ítem queda marcado y bloqueado
        try {
            await actualizarChecklistItem(item.id, { presentado: !item.presentado, idArchivo: null });
            recargarTramite();
        } catch (err) {
            console.error('Error al actualizar checklist:', err);
        }
    };
    const adjuntarArchivo = async (item, file) => {
        if (!file) return;
        setSubiendoItem(item.id);
        try {
            const resp = await subirArchivo(idTramite, file);
            const idArchivo = resp?.idArchivo ?? null;
            await actualizarChecklistItem(item.id, { presentado: true, idArchivo });
            await Promise.all([recargarTramite(), recargarArchivos()]);
        } catch (err) {
            console.error('Error al adjuntar archivo:', err);
            toast.error('No se pudo adjuntar el archivo');
        } finally {
            setSubiendoItem(null);
        }
    };
    const quitarAdjunto = (item) => {
        showModal({
            title: 'Eliminar documento',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: `¿Seguro que querés eliminar el archivo adjunto de "${item.nombre}"?`,
            onConfirm: async () => {
                try {
                    await eliminarArchivo(item.idArchivo);
                    await actualizarChecklistItem(item.id, { presentado: false, idArchivo: null });
                    await Promise.all([recargarTramite(), recargarArchivos()]);
                    toast.info('Documento eliminado');
                } catch (err) {
                    console.error('Error al eliminar documento:', err);
                    toast.error('No se pudo eliminar el documento');
                }
            },
        });
    };

    // ---- Otros documentos (no vinculados al checklist) ----
    const subirGeneral = async (file) => {
        if (!file) return;
        setSubiendoGeneral(true);
        try {
            await subirArchivo(idTramite, file);
            await recargarArchivos();
            toast.success('Archivo subido');
        } catch (err) {
            console.error('Error al subir archivo:', err);
            toast.error('No se pudo subir el archivo');
        } finally {
            setSubiendoGeneral(false);
        }
    };
    const eliminarGeneral = (idArchivo) => {
        showModal({
            title: 'Eliminar archivo',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: '¿Seguro que querés eliminar este archivo? Esta acción no se puede deshacer.',
            onConfirm: async () => {
                try {
                    await eliminarArchivo(idArchivo);
                    await recargarArchivos();
                    toast.info('Archivo eliminado');
                } catch (err) {
                    console.error('Error al eliminar archivo:', err);
                    toast.error('No se pudo eliminar el archivo');
                }
            },
        });
    };

    // ---- Comentarios (nota editable con firma, igual que ART/RTI/Jubilación/PUAM) ----
    const handleGuardarComentario = async (nuevoComentario) => {
        try {
            await actualizarComentariosReconocimiento(idTramite, nuevoComentario);
            await recargarTramite();
            toast.success('Comentario actualizado correctamente');
        } catch (error) {
            console.error('Error al guardar comentario:', error);
            toast.error('No se pudo guardar el comentario');
            throw error;
        }
    };

    // ---- Editar datos del trámite ----
    // El subtipo es fijo desde la creación (patrón Jubilación). Solo tieneRelacionEmpleador
    // (dentro de SERVICIO_DOMESTICO) puede cambiar el checklist, por eso el 409 de confirmación.
    const handleModificar = () => {
        setTramiteEdit({
            organismo: tramite.organismo || '',
            detalleReconocimiento: tramite.detalleReconocimiento || '',
            tieneRelacionEmpleador: !!tramite.tieneRelacionEmpleador,
            etiqueta: tramite.etiqueta || '',
        });
        setEditMode(true);
    };
    const handleGuardarDatos = async (confirmarRegenerarChecklist = false) => {
        setGuardandoDatos(true);
        try {
            await actualizarTramiteReconocimiento(idTramite, {
                ...tramiteEdit,
                subtipo: tramite.subtipo,
                organismo: tramiteEdit.organismo || null,
                confirmarRegenerarChecklist,
            });
            await recargarTramite();
            setEditMode(false);
            toast.success('Datos del trámite actualizados');
        } catch (error) {
            if (error.response?.status === 409 && error.response?.data?.requiereConfirmacion) {
                const items = error.response.data.items || [];
                showModal({
                    title: 'Confirmar cambios en el checklist',
                    type: 'warning',
                    showCancel: true,
                    confirmText: 'Sí, continuar',
                    cancelText: 'Cancelar',
                    confirmVariant: 'warning',
                    message: (
                        <div>
                            <p>Con estos cambios, los siguientes formularios del checklist dejan de requerirse, pero ya tienen un archivo adjunto:</p>
                            <ul className="mb-2">
                                {items.map((it) => (
                                    <li key={it.id}>
                                        <strong>{it.nombre}</strong> — archivo: {it.nombreArchivo || `#${it.idArchivo}`}
                                    </li>
                                ))}
                            </ul>
                            <p className="mb-0">El archivo no se elimina: va a quedar disponible en "Otros documentos". ¿Confirmás continuar?</p>
                        </div>
                    ),
                    onConfirm: () => handleGuardarDatos(true),
                });
            } else {
                console.error('Error al guardar datos:', error);
                toast.error(error.response?.data || 'No se pudieron guardar los datos');
            }
        } finally {
            setGuardandoDatos(false);
        }
    };

    // ---- Resolución del reconocimiento ----
    // Payload de la resolución tal como está guardada hoy: lo usan adjuntar/quitar archivo, que
    // solo tocan el adjunto y no deben pisar el resto de los campos.
    const payloadResolucionActual = () => ({
        aniosReconocidos: tramite.aniosReconocidos ?? null,
        mesesReconocidos: tramite.mesesReconocidos ?? null,
        diasReconocidos: tramite.diasReconocidos ?? null,
        observacionesResolucion: tramite.observacionesResolucion ?? null,
        cuantoFalta: tramite.cuantoFalta ?? null,
        resultadoResolucion: tramite.resultadoResolucion ?? null,
        fechaResolucion: tramite.fechaResolucion ?? null,
    });
    const handleModificarResolucion = () => {
        setResolucionEdit({
            aniosReconocidos: tramite.aniosReconocidos ?? '',
            mesesReconocidos: tramite.mesesReconocidos ?? '',
            diasReconocidos: tramite.diasReconocidos ?? '',
            observacionesResolucion: tramite.observacionesResolucion || '',
            cuantoFalta: tramite.cuantoFalta || '',
            resultadoResolucion: tramite.resultadoResolucion || '',
            fechaResolucion: formatLocalFecha(tramite.fechaResolucion),
        });
        setResolucionEditMode(true);
    };
    const handleGuardarResolucion = async () => {
        setGuardandoResolucion(true);
        try {
            await guardarResolucionReconocimiento(idTramite, {
                ...resolucionEdit,
                aniosReconocidos: aEnteroONulo(resolucionEdit.aniosReconocidos),
                mesesReconocidos: aEnteroONulo(resolucionEdit.mesesReconocidos),
                diasReconocidos: aEnteroONulo(resolucionEdit.diasReconocidos),
                observacionesResolucion: resolucionEdit.observacionesResolucion || null,
                resultadoResolucion: resolucionEdit.resultadoResolucion || null,
                fechaResolucion: resolucionEdit.fechaResolucion || null,
                idArchivoResolucion: tramite.idArchivoResolucion || null,
            });
            await recargarTramite();
            setResolucionEditMode(false);
            toast.success('Resolución actualizada');
        } catch (error) {
            console.error('Error al guardar la resolución:', error);
            toast.error(error.response?.data || 'No se pudo guardar la resolución');
        } finally {
            setGuardandoResolucion(false);
        }
    };
    const adjuntarResolucion = async (file) => {
        if (!file) return;
        setSubiendoResolucion(true);
        try {
            const resp = await subirArchivo(idTramite, file);
            const idArchivo = resp?.idArchivo ?? null;
            await guardarResolucionReconocimiento(idTramite, {
                ...payloadResolucionActual(),
                idArchivoResolucion: idArchivo,
            });
            await Promise.all([recargarTramite(), recargarArchivos()]);
        } catch (err) {
            console.error('Error al adjuntar el archivo de la resolución:', err);
            toast.error('No se pudo adjuntar el archivo de la resolución');
        } finally {
            setSubiendoResolucion(false);
        }
    };
    const quitarResolucionAdjunta = () => {
        showModal({
            title: 'Eliminar archivo de la resolución',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: '¿Seguro que querés eliminar el archivo de la resolución adjuntado?',
            onConfirm: async () => {
                try {
                    await eliminarArchivo(tramite.idArchivoResolucion);
                    await guardarResolucionReconocimiento(idTramite, {
                        ...payloadResolucionActual(),
                        idArchivoResolucion: null,
                    });
                    await Promise.all([recargarTramite(), recargarArchivos()]);
                    toast.info('Archivo de la resolución eliminado');
                } catch (err) {
                    console.error('Error al eliminar el archivo de la resolución:', err);
                    toast.error('No se pudo eliminar el archivo de la resolución');
                }
            },
        });
    };

    const verDetalleCliente = (cuil) => navigate(`/clientes/vista-detallada/${cuil.toString()}`);

    if (loading) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}></div>
                <p className="mt-3 fw-semibold" style={{ color: '#6B7280' }}>Cargando trámite...</p>
            </div>
        );
    }
    if (!tramite) {
        return <div className="alert alert-danger mt-4">No se encontró el trámite.</div>;
    }

    const cliente = tramite.cliente || {};
    const formularios = tramite.formularios || [];
    const idsEnChecklist = new Set([
        ...formularios.map(f => f.idArchivo).filter(Boolean),
        ...(tramite.idArchivoResolucion ? [tramite.idArchivoResolucion] : []),
    ]);
    const archivosOtros = archivos.filter(a => !idsEnChecklist.has(a.idArchivo));
    const nombrePorId = archivos.reduce((acc, a) => { acc[a.idArchivo] = a.nombre; return acc; }, {});
    const formatTamano = (bytes) => (bytes == null ? '' : (bytes / (1024 * 1024)).toFixed(2) + ' MB');

    const resultadoBanner = RESULTADO_BANNER[tramite.resultadoResolucion] || null;
    const resultadoMeta = tramite.resultadoResolucion ? RESULTADO_META[tramite.resultadoResolucion] : null;

    // Total de servicios reconocidos por la resolución, ya formateado ("21 años 8 meses 8 días")
    const totalReconocido = formatearTotalReconocido(
        tramite.aniosReconocidos, tramite.mesesReconocidos, tramite.diasReconocidos);

    // Fondo tenue de la sección Resolución según el resultado (sutil, no un color fuerte) y
    // tarjetas internas neutras acordes al resto de la estética (blancas, con acento de color
    // solo en el ícono y el borde), patrón similar a los recuadros de PanelResumenAportes.
    const FONDO_TENUE_POR_RESULTADO = {
        FAVORABLE: '#f0fdf4',
        DESFAVORABLE: '#fef2f2',
        PENDIENTE: '#fffbeb',
    };
    const fondoSeccionResolucion = FONDO_TENUE_POR_RESULTADO[tramite.resultadoResolucion] || '#ffffff';
    const acentoResolucion = resultadoMeta ? resultadoMeta.color : '#6b7280';
    const tileResolucion = (titulo, valor, icono) => (
        <div className="col-md-6 col-lg-4">
            <div className="card shadow-sm h-100" style={{ border: `1px solid ${acentoResolucion}33`, borderRadius: '12px', background: 'white' }}>
                <div className="card-body" style={{ padding: '1.1rem' }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{titulo}</div>
                            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>{valor || 'Sin especificar'}</div>
                        </div>
                        <i className={`bi ${icono}`} style={{ fontSize: '1.75rem', color: acentoResolucion, opacity: 0.6 }}></i>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="container mt-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div style={{ flex: '1 1 20rem', minWidth: 0 }}>
                    <div className="d-flex align-items-center flex-wrap gap-3 mb-1">
                        <h2 className="fw-bold text-primary mb-0">Detalles del Trámite de Reconocimiento de Servicios</h2>
                        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle rounded-pill fs-6 px-3">
                            RECONOCIMIENTO
                        </span>
                        <EtiquetaTramite etiqueta={tramite.etiqueta} />
                    </div>
                    {cliente?.apellido && cliente?.nombre && (
                        <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                            <h5 className="text-secondary fw-semibold mb-0">
                                <PersonSearchIcon fontSize="small" className="me-1 mb-1" />
                                {apellidoNombre(cliente.apellido, cliente.nombre)}
                            </h5>
                            {cliente.telefonoPropio && (
                                <WhatsAppHeaderButton
                                    phone={cliente.telefonoPropio}
                                    clientName={`${cliente.nombre} ${cliente.apellido}`}
                                    onClick={() => window.dispatchEvent(
                                        new CustomEvent('openAdminSidebar', { detail: { tab: 'whatsapp' } })
                                    )}
                                />
                            )}
                        </div>
                    )}
                    <PanelUbicacionTramite tramite={tramite} onUpdated={setTramite} />
                </div>
                <div className="d-flex gap-2 mt-1 align-items-center flex-wrap">
                    <BotonGenerarCaratula
                        tipo="RECONOCIMIENTO"
                        idTramite={tramite.idTramite}
                        onGenerado={recargarArchivos}
                    />
                    <button
                        className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill"
                        onClick={() => setMostrarModalTurno(true)}
                    >
                        <i className="bi bi-calendar-plus"></i> Crear turno
                    </button>
                    <button
                        className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill"
                        onClick={volverAtras}
                    >
                        <ArrowBackIcon fontSize="small" /> Volver
                    </button>
                </div>
            </div>

            {/* Banner de resultado (patrón ART/RTI): vistoso cuando la resolución ya salió */}
            {resultadoBanner && (
                <div className={`alert alert-${resultadoBanner.bg} border-0 rounded-4 shadow-lg p-4 mb-4 d-flex align-items-center`}>
                    <span className="me-3">{resultadoBanner.icon}</span>
                    <div>
                        <h4 className="fw-bold mb-1">{resultadoBanner.titulo}</h4>
                        <p className="mb-0">
                            {totalReconocido
                                ? <>Total de servicios reconocidos: <strong>{totalReconocido}</strong></>
                                : 'Sin total de servicios cargado.'}
                        </p>
                    </div>
                </div>
            )}

            {/* El de arriba solo cubre favorable/desfavorable (resultadoResolucion); Cancelado no
                tiene banner propio en este módulo, así que se usa el genérico solo para ese caso
                (si resultadoBanner ya se está mostrando, no hace falta el segundo). */}
            {!resultadoBanner && (
                <BannerEstadoFinal idTramite={tramite.idTramite} nombreEstadoActual={tramite.nombreEstadoActual} />
            )}

            {/* Estado + historial */}
            <div
                className="card shadow-sm border-0 rounded-4 p-4 mb-3"
                style={{ backgroundColor: '#f8f9fa', cursor: 'pointer', transition: 'all 0.2s ease' }}
                onClick={() => setModalHistorialAbierto(true)}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e9ecef'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
            >
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                        <h5 className="fw-bold text-secondary mb-0">Estado</h5>
                        {resultadoMeta ? (
                            <span className="badge rounded-pill fs-6 px-3" style={{ background: resultadoMeta.bg, color: resultadoMeta.color, border: `1px solid ${resultadoMeta.color}33` }}>
                                Resolución {resultadoMeta.label}
                            </span>
                        ) : (
                            <span className="badge bg-success bg-opacity-10 text-success border border-success-subtle rounded-pill fs-6 px-3">
                                {tramite.nombreEstadoActual || 'Ingreso del Trámite'}
                            </span>
                        )}
                        {tramite.fechaEstadoActual && (
                            <span className="text-muted small">
                                {new Date(tramite.fechaEstadoActual).toLocaleDateString('es-ES')}
                            </span>
                        )}
                    </div>
                    <span className="text-primary">
                        <i className="bi bi-arrow-right-circle me-1"></i>Ver historial
                    </span>
                </div>
            </div>

            {/* Nota editable de comentarios (con firma, igual que ART/RTI/Jubilación/PUAM) */}
            <NotaComentario
                valor={tramite.comentarios || ''}
                onGuardar={handleGuardarComentario}
                label="Comentarios del Trámite de Reconocimiento de Servicios"
                color="blue"
                nombreUsuario={user?.nombre || ''}
            />

            {/* Cobranza del trámite: desplegable, debajo del estado y los comentarios */}
            <SeccionCobranzaTramite tramite={tramite} />

            {/* Turno en ANSES: hito propio del trámite. Al cargarlo se genera la citación al
                estudio en el turnero y el evento de agenda. */}
            <TarjetaTurnoAnses
                idTramite={tramite.idTramite}
                cuilCliente={tramite.idCliente || cliente.cuil}
                nombreArea="este trámite de Reconocimiento de Servicios"
                nombreEstadoActual={tramite.nombreEstadoActual}
                onCambio={refrescarPorTurnoAnses}
                onEstadoCambiado={refrescarPorTurnoAnses}
            />
            {enSeguimientoDeExpediente(tramite.nombreEstadoActual) && <ExpedientesAnsesTramite idTramite={tramite.idTramite} />}

            {/* Eventos del Trámite */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold text-primary mb-0">Eventos del Trámite</h4>
                            <button
                                className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill fw-semibold"
                                onClick={() => {
                                    const hoy = new Date();
                                    setFechaSeleccionadaEvento(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()));
                                    setMostrarModalEvento(true);
                                }}
                            >
                                <span style={{ fontSize: '1.2rem' }}>+</span> Crear Evento
                            </button>
                        </div>
                        {cargandoEventos ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status"></div>
                                <p className="mt-2 text-muted">Cargando eventos...</p>
                            </div>
                        ) : eventos.length === 0 ? (
                            <div className="alert alert-info border-0 rounded-3 text-center">
                                <p className="mb-0">No hay eventos registrados para este trámite</p>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {eventos.map(evento => (
                                    <div key={evento.idEvento} className="col-md-6 col-lg-4">
                                        <div className="card h-100 border rounded-3 p-3 shadow-sm">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h6 className="fw-bold text-primary mb-0">{evento.titulo}</h6>
                                                {evento.tipoEvento && <span className="badge bg-primary rounded-pill">{evento.tipoEvento}</span>}
                                            </div>
                                            {evento.descripcion && <p className="text-muted small mb-2">{evento.descripcion}</p>}
                                            <div className="mt-auto">
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <small className="text-muted">📅</small>
                                                    <small className="fw-semibold">{formatearFechaEvento(evento.fecha)}</small>
                                                </div>
                                                {evento.todoElDia ? (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">🕐</small>
                                                        <small className="text-success fw-semibold">Todo el día</small>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">🕐</small>
                                                        <small>{evento.horaInicio} - {evento.horaFin}</small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Info del Trámite + Info del Cliente */}
            <div className="row g-4">
                <div className="col-lg-6">
                    <div className="card shadow-lg border-0 rounded-4 p-4 h-100">
                        <h4 className="fw-bold text-primary mb-3">
                            <i className="bi bi-person-vcard me-2"></i>
                            Información del Trámite
                        </h4>
                        {!editMode ? (
                            <>
                                <p><strong>Subtipo:</strong> {LABEL_SUBTIPO[tramite.subtipo] || tramite.subtipo || 'N/A'}</p>
                                <p><strong>Organismo:</strong> {tramite.organismo === 'ANSES' ? 'ANSES' : tramite.organismo === 'PROVINCIA' ? 'Provincia' : 'Sin especificar'}</p>
                                {tramite.detalleReconocimiento && (
                                    <p><strong>Detalle:</strong> {tramite.detalleReconocimiento}</p>
                                )}
                                {tramite.subtipo === 'SERVICIO_DOMESTICO' && (
                                    <p><strong>Tiene relación con el empleador:</strong> {tramite.tieneRelacionEmpleador ? 'Sí' : 'No'}</p>
                                )}
                                {tramite.etiqueta && <p><strong>Etiqueta:</strong> {tramite.etiqueta}</p>}
                                <ChecklistTramite tramite={tramite} onActualizado={recargarTramite} />
                                <TarjetaCobroTramite
                                    tramite={tramite}
                                    onPactoGenerado={() => Promise.all([recargarTramite(), recargarArchivos()])}
                                />
                                <div className="mt-3 d-flex gap-2 flex-wrap">
                                    <button className="btn btn-outline-primary rounded-pill px-4 fw-semibold" onClick={handleModificar}>
                                        <i className="bi bi-pencil me-2"></i>Editar datos
                                    </button>
                                    <BotonCancelarReactivarTramite tramite={tramite} onActualizado={recargarTramite} />
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="mb-3"><strong>Subtipo:</strong> {LABEL_SUBTIPO[tramite.subtipo] || tramite.subtipo} <span className="text-muted small">(no editable)</span></p>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Organismo</label>
                                    <select className="form-select" value={tramiteEdit.organismo || ''}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, organismo: e.target.value }))}>
                                        <option value="">Sin especificar</option>
                                        <option value="ANSES">ANSES</option>
                                        <option value="PROVINCIA">Provincia</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Detalle de lo que se reconoce</label>
                                    <input type="text" className="form-control" value={tramiteEdit.detalleReconocimiento || ''}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, detalleReconocimiento: e.target.value }))} />
                                </div>
                                {tramite.subtipo === 'SERVICIO_DOMESTICO' && (
                                    <div className="form-check mb-3">
                                        <input className="form-check-input" type="checkbox" id="edit-relacion-empleador"
                                            checked={!!tramiteEdit.tieneRelacionEmpleador}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, tieneRelacionEmpleador: e.target.checked }))} />
                                        <label className="form-check-label" htmlFor="edit-relacion-empleador">Tiene relación con el empleador (PS 2.93 en vez de PS 2.94)</label>
                                    </div>
                                )}
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Etiqueta</label>
                                    <input type="text" className="form-control" value={tramiteEdit.etiqueta || ''}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, etiqueta: e.target.value }))} />
                                </div>
                                <div className="d-flex gap-2">
                                    <button className="btn btn-primary rounded-pill px-4 fw-semibold" onClick={() => handleGuardarDatos()} disabled={guardandoDatos}>
                                        {guardandoDatos ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle me-2"></i>Guardar</>}
                                    </button>
                                    <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setEditMode(false)}>Cancelar</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="col-lg-6">
                    <div className="card shadow-lg border-0 rounded-4 p-4 h-100">
                        <h4 className="fw-bold mb-3" style={{ color: '#6c757d' }}>Información del Cliente</h4>
                        <p><strong>CUIL:</strong> {cliente.cuil || 'N/A'}</p>
                        <p><strong>Nombre:</strong> {cliente.nombre || 'N/A'}</p>
                        <p><strong>Apellido:</strong> {cliente.apellido || 'N/A'}</p>
                        <p><strong>Oficio / Actividad:</strong> {cliente.oficio || 'N/A'}</p>
                        <p><strong>Teléfono:</strong> {cliente.telefonoPropio || 'N/A'}</p>
                        {cliente.fechaNacimiento && (
                            <p><strong>Fecha de nacimiento:</strong> {new Date(cliente.fechaNacimiento).toLocaleDateString('es-ES')}</p>
                        )}
                        {cliente.cuil && (
                            <div className="mt-3 d-flex justify-content-center">
                                <button
                                    className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                    onClick={() => verDetalleCliente(cliente.cuil)}
                                >
                                    <PersonSearchIcon fontSize="medium" /> Ver Detalle del Cliente
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Expedientes de ANSES: acá, debajo de los datos del trámite y del cliente; arriba solo
                mientras el trámite está en "Seguimiento de Expediente". */}
            {!enSeguimientoDeExpediente(tramite.nombreEstadoActual) && <ExpedientesAnsesTramite idTramite={tramite.idTramite} />}

            {/* Resolución del Reconocimiento */}
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4" style={{ background: fondoSeccionResolucion }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold text-primary mb-0"><i className="bi bi-file-earmark-check me-2"></i>Resolución</h4>
                            {tramite.resultadoResolucion && (
                                <span
                                    className="badge rounded-pill"
                                    style={{ background: RESULTADO_META[tramite.resultadoResolucion]?.bg, color: RESULTADO_META[tramite.resultadoResolucion]?.color }}
                                >
                                    {RESULTADO_META[tramite.resultadoResolucion]?.label || tramite.resultadoResolucion}
                                </span>
                            )}
                        </div>

                        {!resolucionEditMode ? (
                            <>
                                <div className="row g-3">
                                    {tileResolucion('TOTAL DE SERVICIOS RECONOCIDOS', totalReconocido, 'bi-calendar-range')}
                                    {tileResolucion('CUÁNTO FALTA PARA LA JUBILACIÓN', tramite.cuantoFalta, 'bi-hourglass-split')}
                                    {tileResolucion('FECHA DE RESOLUCIÓN', tramite.fechaResolucion ? new Date(tramite.fechaResolucion + 'T00:00:00').toLocaleDateString('es-ES') : null, 'bi-calendar-check')}
                                </div>

                                {tramite.observacionesResolucion && (
                                    <div className="card shadow-sm mt-3" style={{ border: `1px solid ${acentoResolucion}33`, borderRadius: '12px', background: 'white' }}>
                                        <div className="card-body" style={{ padding: '1.1rem' }}>
                                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                <i className="bi bi-chat-left-text me-2"></i>Observaciones
                                            </div>
                                            <div style={{ color: '#1f2937', whiteSpace: 'pre-wrap' }}>{tramite.observacionesResolucion}</div>
                                        </div>
                                    </div>
                                )}

                                <div className="d-flex align-items-center gap-2 mt-3 flex-wrap">
                                    <button className="btn btn-outline-primary rounded-pill px-4 fw-semibold" onClick={handleModificarResolucion}>
                                        <i className="bi bi-pencil me-2"></i>Editar resolución
                                    </button>
                                    {tramite.idArchivoResolucion ? (
                                        <>
                                            <button className="btn btn-sm btn-outline-primary rounded-pill"
                                                onClick={() => descargarPorId(tramite.idArchivoResolucion)}
                                                title={nombrePorId[tramite.idArchivoResolucion] || 'Descargar'}>
                                                <i className="bi bi-download me-1"></i>
                                                {nombrePorId[tramite.idArchivoResolucion] || 'Descargar resolución'}
                                            </button>
                                            <button className="btn btn-sm btn-link text-danger p-0" title="Quitar adjunto"
                                                onClick={quitarResolucionAdjunta}>
                                                <i className="bi bi-x-circle"></i>
                                            </button>
                                        </>
                                    ) : (
                                        <label className="btn btn-sm btn-outline-secondary rounded-pill mb-0">
                                            {subiendoResolucion
                                                ? <span className="spinner-border spinner-border-sm" role="status"></span>
                                                : <><i className="bi bi-paperclip me-1"></i>Adjuntar archivo de la resolución</>}
                                            <input type="file" accept="application/pdf" hidden
                                                onChange={(e) => adjuntarResolucion(e.target.files[0])} />
                                        </label>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Total de servicios reconocidos</label>
                                    <div className="row g-2">
                                        <div className="col-4">
                                            <div className="input-group">
                                                <input type="number" min="0" className="form-control" value={resolucionEdit.aniosReconocidos}
                                                    onChange={(e) => setResolucionEdit(p => ({ ...p, aniosReconocidos: e.target.value }))} />
                                                <span className="input-group-text">años</span>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="input-group">
                                                <input type="number" min="0" className="form-control" value={resolucionEdit.mesesReconocidos}
                                                    onChange={(e) => setResolucionEdit(p => ({ ...p, mesesReconocidos: e.target.value }))} />
                                                <span className="input-group-text">meses</span>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="input-group">
                                                <input type="number" min="0" className="form-control" value={resolucionEdit.diasReconocidos}
                                                    onChange={(e) => setResolucionEdit(p => ({ ...p, diasReconocidos: e.target.value }))} />
                                                <span className="input-group-text">días</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-text">Total que informa la resolución (ej: 21 años 8 meses 8 días). El detalle por período va en el archivo adjunto.</div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Cuánto falta para la jubilación</label>
                                    <input type="text" className="form-control" value={resolucionEdit.cuantoFalta}
                                        onChange={(e) => setResolucionEdit(p => ({ ...p, cuantoFalta: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Resultado</label>
                                    <select className="form-select" value={resolucionEdit.resultadoResolucion}
                                        onChange={(e) => setResolucionEdit(p => ({ ...p, resultadoResolucion: e.target.value }))}>
                                        <option value="">Sin especificar</option>
                                        <option value="FAVORABLE">Favorable</option>
                                        <option value="DESFAVORABLE">Desfavorable</option>
                                        <option value="PENDIENTE">Pendiente</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Fecha de resolución</label>
                                    <input type="date" className="form-control" value={resolucionEdit.fechaResolucion}
                                        onChange={(e) => setResolucionEdit(p => ({ ...p, fechaResolucion: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Observaciones</label>
                                    <textarea className="form-control" rows={3} maxLength={500}
                                        value={resolucionEdit.observacionesResolucion}
                                        onChange={(e) => setResolucionEdit(p => ({ ...p, observacionesResolucion: e.target.value }))}
                                        placeholder="Opcional: aclaraciones sobre la resolución" />
                                </div>
                                <div className="d-flex gap-2">
                                    <button className="btn btn-primary rounded-pill px-4 fw-semibold" onClick={handleGuardarResolucion} disabled={guardandoResolucion}>
                                        {guardandoResolucion ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle me-2"></i>Guardar</>}
                                    </button>
                                    <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setResolucionEditMode(false)}>Cancelar</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Documentación del Trámite: checklist de formularios + otros documentos (un solo lugar) */}
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <h4 className="fw-bold text-primary mb-3"><i className="bi bi-folder2-open me-2"></i>Documentación del Trámite</h4>

                        {/* Formularios requeridos */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="fw-bold text-secondary mb-0"><i className="bi bi-check2-square me-2"></i>Formularios requeridos</h6>
                            <span className={`badge ${claseBadgeContador(formularios.filter(f => f.presentado).length, formularios.length)}`}>{formularios.filter(f => f.presentado).length}/{formularios.length}</span>
                        </div>
                        {formularios.length === 0 ? (
                            <div className="alert alert-info border-0 rounded-3 text-center mb-0">
                                <p className="mb-0">No hay formularios en el checklist.</p>
                            </div>
                        ) : (
                            <ul className="list-group list-group-flush">
                                {formularios.map(f => (
                                    <li key={f.id} className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2 px-0">
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id={`chk-${f.id}`}
                                                checked={!!f.presentado} disabled={!!f.idArchivo}
                                                onChange={() => togglePresentado(f)} />
                                            <label className="form-check-label" htmlFor={`chk-${f.id}`}
                                                style={{ textDecoration: f.presentado ? 'line-through' : 'none', color: f.presentado ? '#6c757d' : 'inherit' }}>
                                                {f.nombre}
                                            </label>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            {f.idArchivo ? (
                                                <>
                                                    <button className="btn btn-sm btn-outline-primary rounded-pill"
                                                        onClick={() => descargarPorId(f.idArchivo)}
                                                        title={nombrePorId[f.idArchivo] || 'Descargar'}>
                                                        <i className="bi bi-download me-1"></i>
                                                        {nombrePorId[f.idArchivo] || 'Descargar'}
                                                    </button>
                                                    <button className="btn btn-sm btn-link text-danger p-0" title="Quitar adjunto"
                                                        onClick={() => quitarAdjunto(f)}>
                                                        <i className="bi bi-x-circle"></i>
                                                    </button>
                                                </>
                                            ) : (
                                                <label className="btn btn-sm btn-outline-secondary rounded-pill mb-0">
                                                    {subiendoItem === f.id
                                                        ? <span className="spinner-border spinner-border-sm" role="status"></span>
                                                        : <><i className="bi bi-paperclip me-1"></i>Adjuntar</>}
                                                    <input type="file" accept="application/pdf" hidden
                                                        onChange={(e) => adjuntarArchivo(f, e.target.files[0])} />
                                                </label>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <hr className="my-4" />

                        {/* Otros documentos (no vinculados a un formulario) */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="fw-bold text-secondary mb-0"><i className="bi bi-paperclip me-2"></i>Otros documentos</h6>
                            <label className="btn btn-sm btn-outline-primary rounded-pill mb-0">
                                {subiendoGeneral
                                    ? <span className="spinner-border spinner-border-sm" role="status"></span>
                                    : <><i className="bi bi-cloud-upload me-1"></i>Subir archivo</>}
                                <input type="file" hidden onChange={(e) => { subirGeneral(e.target.files[0]); e.target.value = ''; }} />
                            </label>
                        </div>
                        {archivosOtros.length === 0 ? (
                            <p className="text-muted fst-italic mb-0">No hay otros documentos cargados.</p>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {archivosOtros.map(a => (
                                    <div key={a.idArchivo} className="d-flex align-items-center justify-content-between border rounded-3 px-3 py-2">
                                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                            <i className="bi bi-file-earmark-text text-primary" style={{ fontSize: '1.25rem' }}></i>
                                            <div style={{ minWidth: 0 }}>
                                                <div className="fw-semibold text-truncate" style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                    onClick={() => descargarPorId(a.idArchivo)} title={a.nombre}>
                                                    {a.nombre}
                                                </div>
                                                <small className="text-muted">{formatTamano(a.tamaño)}</small>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                            <button className="btn btn-sm" style={{ color: '#0056b3', border: 'none' }} title="Descargar"
                                                onClick={() => descargarPorId(a.idArchivo)}>
                                                <i className="bi bi-download"></i>
                                            </button>
                                            <button className="btn btn-sm" style={{ color: '#dc3545', border: 'none' }} title="Eliminar"
                                                onClick={() => eliminarGeneral(a.idArchivo)}>
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal crear evento */}
            <CrearEventoModal
                isOpen={mostrarModalEvento}
                onClose={() => setMostrarModalEvento(false)}
                onSave={handleGuardarEvento}
                selectedDate={fechaSeleccionadaEvento}
                idTramite={idTramite}
            />

            {/* Acciones irreversibles (cambiar tipo / eliminar definitivamente).
                El propio panel decide si mostrarse segun el rol. */}
            <PanelAccionesAvanzadasTramite
                idTramite={tramite?.idTramite}
                tipoTramite="RECONOCIMIENTO"
            />

            {/* Modal historial de estados (ámbito RECONOCIMIENTO) */}
            <HistorialTramite
                isOpen={modalHistorialAbierto}
                onClose={() => setModalHistorialAbierto(false)}
                idTramite={idTramite}
                onEstadoCambiado={recargarTramite}
                ambitoFiltro="RECONOCIMIENTO"
            />

            {/* Modal crear turno (reutiliza el turnero de Recepción, prellenado con el cliente) */}
            {mostrarModalTurno && (
                <div className={turneroStyles.overlay} onClick={() => setMostrarModalTurno(false)}>
                    <div className={turneroStyles.contenidoModal} onClick={(e) => e.stopPropagation()}>
                        <ModalCrearTurno
                            celdaSel={null}
                            valoresIniciales={{
                                nombre: cliente.nombre || '',
                                apellido: cliente.apellido || '',
                                cuil: cliente.cuil || '',
                                telefono: cliente.telefonoPropio || '',
                                tipoTramite: 'RECONOCIMIENTO',
                            }}
                            onCerrar={() => setMostrarModalTurno(false)}
                            onCreado={() => { setMostrarModalTurno(false); toast.success('Turno creado correctamente'); }}
                        />
                    </div>
                </div>
            )}

            <CustomModal {...modalState} onHide={hideModal} />
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
            <FeedbackButton tramite={tramite} />
        </div>
    );
}

export default VistaDetalladaTramiteReconocimiento;
